// Hooks
import { useQuery } from '@hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useUserQuery, useUserMutation } from '@features/redux/apis/auth';

// Base Components
import { Grid } from '@mui/material';
import { ManageForm } from '@components/forms/ManageForm';
import { FormCheckBox } from '@components/forms/FormCheckBox';
import { FormTextArea } from '@components/forms/text/base/FormTextArea';
import { FormRequiredText } from '@components/forms/text/base/FormRequiredText';
import { FormActions } from '@components/forms/FormActions';

// Fixed Scope Components
import { MultiOrgDropdowns } from '@components/forms/dropdowns/MultiOrgDropdowns';
import { UserEffects } from './UserEffects';
import { ResetPassword } from './ResetPassword';

// Utils, Consts
import { Operation } from '@/types/general';
import { Query } from '@features/redux/apis/utils';
import { AppRoutes } from '@consts/routes';
import { Validate } from '@utils/validate';

// Types
import type { User as UserType } from '@features/redux/apis/auth';
import { store, type RootState } from '@features/redux/store';
import type { Option } from '@components/forms/dropdowns/base/FormMultiSelect';

const useUser = Query.withDraft(useUserQuery, {
    given_name: '',
    family_name: '',
    email: '',
    password: '',
    'custom:PriceEditRole': '0',
    'custom:UploaderRole': '0',
    'custom:UserEditRole': '0',
    'custom:ProductEditRole': '0',
    'custom:StoreEditRole': '0',
    'custom:Notes': '',
    'custom:Companies': '',
    'custom:Locations': '',
    'custom:Resellers': '',
} as any as UserType);

export const User = () => {
    const navigate = useNavigate();
    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const userId = useQuery().get('id');
    const operation = userId ? Operation.Edit : Operation.Create;

    const { data: user, draft, isLoading } = useUser({ id: userId! }, { skip: !userId });
    const [draftState] = draft;

    const [mutate] = useUserMutation({
        fixedCacheKey: operation === Operation.Edit ? 'UserMutation' : 'UserCreation',
    });

    const selectedState = useState<Record<'companies' | 'resellers' | 'locations', Option[]>>({
        companies: [],
        resellers: [],
        locations: [],
    });

    const [selected] = selectedState;

    return (
        <>
            <UserEffects />

            <ManageForm title={operation === Operation.Create ? 'Create New User' : 'Edit User'} loading={isLoading}>
                <FormRequiredText
                    size={6}
                    label='First Name'
                    draft={draft}
                    stateKey='given_name'
                    helperText={{
                        valid: '',
                        invalid: 'Please provide a valid first name.',
                    }}
                    validateFn={Validate.name}
                />

                <FormRequiredText
                    size={6}
                    label='Last Name'
                    draft={draft}
                    stateKey='family_name'
                    helperText={{
                        valid: '',
                        invalid: 'Please provide a valid last name.',
                    }}
                    validateFn={Validate.name}
                />

                <FormRequiredText
                    size={operation === Operation.Edit ? 8 : 6}
                    helperText={{
                        valid: '',
                        invalid: 'Please enter a proper email address',
                    }}
                    label='Email Address'
                    stateKey={'email'}
                    type={'email'}
                    draft={draft}
                    validateFn={Validate.Auth.email}
                    disabled={operation === Operation.Edit}
                />

                {operation === Operation.Edit && <ResetPassword user={user!} />}

                {operation === Operation.Create && (
                    <FormRequiredText
                        size={6}
                        helperText={{
                            valid: '',
                            invalid: 'Please enter a password with 6 characters or more',
                        }}
                        label='Password'
                        draft={draft}
                        stateKey={'password'}
                        type={'password'}
                        disabled={false}
                        validateFn={Validate.Auth.password}
                    />
                )}

                <MultiOrgDropdowns
                    state={selectedState}
                    populate={{
                        companies: user?.['custom:Companies'],
                        resellers: user?.['custom:Resellers'],
                        locations: user?.['custom:Locations'],
                    }}
                />

                <Grid container sx={{ margin: '20px' }}>
                    <FormCheckBox
                        hidden={!role.users.edit}
                        label='Edit Users'
                        draft={draft}
                        stateKey={'custom:UserEditRole'}
                        values={{
                            checked: '1',
                            unchecked: '0',
                        }}
                    />

                    <FormCheckBox
                        hidden={!role.prices.edit}
                        label='Edit Prices'
                        draft={draft}
                        stateKey={'custom:PriceEditRole'}
                        values={{
                            checked: '1',
                            unchecked: '0',
                        }}
                    />

                    <FormCheckBox
                        hidden={!role.stores.edit}
                        label='Edit Stores'
                        draft={draft}
                        stateKey={'custom:StoreEditRole'}
                        values={{
                            checked: '1',
                            unchecked: '0',
                        }}
                    />

                    <FormCheckBox
                        hidden={!role.products.edit}
                        label='Edit Products'
                        draft={draft}
                        stateKey={'custom:ProductEditRole'}
                        values={{
                            checked: '1',
                            unchecked: '0',
                        }}
                    />

                    <FormCheckBox
                        hidden={!role.uploading.edit}
                        label='Edit Designs'
                        draft={draft}
                        stateKey={'custom:UploaderRole'}
                        values={{
                            checked: '1',
                            unchecked: '0',
                        }}
                    />
                </Grid>

                {role.isAdmin && <FormTextArea draft={draft} stateKey='custom:Notes' helperText='Notes' />}

                <Grid item xs={12} sm={12} md={12} display='flex' gap='10px'>
                    <FormActions
                        onSave={() => {
                            const userToken = store.getState().auth.TokenPayload;

                            const companies = selected.companies.map(({ id }) => id).join(',');
                            const locations = selected.locations.map(({ id }) => id).join(',');
                            const resellers = selected.resellers.map(({ id }) => id).join(',');

                            const user: UserType = {
                                ...draftState,
                                Username: draftState.email,
                                // ? If "All" was selected, give the user all the companies/locations/resellers
                                // ? the creating user has.
                                // ! This is not the cleanest way to do this, but is the most atomic right now
                                'custom:Companies': companies === '*' ? userToken!['custom:Companies']! : companies,
                                'custom:Locations': locations === '*' ? userToken!['custom:Locations']! : locations,
                                'custom:Resellers': resellers === '*' ? userToken!['custom:Resellers']! : resellers,
                            };

                            mutate({
                                method: operation === Operation.Create ? 'POST' : 'PUT',
                                user,
                            });
                        }}
                        onCancel={() => navigate(AppRoutes.Users)}
                        isUpdate={operation === Operation.Edit}
                        disabled={
                            !Validate.Auth.user(draftState) ||
                            !Object.values(selected).every((arr) => Boolean(arr.length)) ||
                            (operation === Operation.Create && !Validate.Auth.password(draftState.password!))
                        }
                    />
                </Grid>
            </ManageForm>
        </>
    );
};
